import { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { locations, propertyTypes } from '../propertyConfig';
import FilterDrawer from './FilterDrawer';
import BrowseQuickFilters, { buildQuickFilterFields } from './BrowseQuickFilters';
import ListingCard from './ListingCard';
import PropertyGoogleMap from './PropertyGoogleMap';
import PropertySpotlightBanner from './PropertySpotlightBanner';
import AdBanner from './AdBanner';
import {
  FilterIcon,
  BellIcon,
  ClockIcon,
  TrendingUpIcon,
  TrendingDownIcon,
  ChevronDownIcon,
  HomeIcon,
} from './IconSet';
import { Breadcrumbs, LoadingSpinner, Pagination, Tooltip } from './ComponentLibrary';
import { placeholderImage } from '../propertyConfig';
import { API_BASE } from '../config';
import SeoHead from './SeoHead';
import { pageMeta } from '../seoUtils';
import { useShortLetProperties } from '../hooks/useShortLetProperties';
import { applyClientRefinements } from '../hooks/usePropertyFilters';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const CANONICAL_LISTING_TYPES = ['rent', 'buy', 'short-let'];

function BrowsePage({
  filterLocation,
  filterArea,
  filterType,
  filterPropertyType,
  sortBy,
  browseAreas,
  setFilterLocation,
  setFilterArea,
  setFilterType,
  setFilterPropertyType,
  setSortBy,
  isFilterDrawerOpen,
  setIsFilterDrawerOpen,
  resetAllFilters,
  filteredProperties,
  error,
  loading,
  minPrice,
  maxPrice,
  minBedrooms,
  minBathrooms,
  postedWithin,
  setMinPrice,
  setMaxPrice,
  setMinBedrooms,
  setMinBathrooms,
  setPostedWithin,
  filterState,
  filterSetters,
  filterAmenities,
  toggleFilterAmenity,
  filterPropertyDocuments,
  toggleFilterPropertyDocument,
  onOpenProperty,
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, authedFetch } = useAuth();
  const { showToast } = useToast();

  // Backs the sitewide WebSite/SearchAction JSON-LD (see seoUtils.js
  // websiteJsonLd) - Google's Sitelinks Search Box sends ?q=<state name>
  // here, so it needs to actually filter something or the rich result would
  // be misleading. Also seeds the listing-type/property-type filter from
  // ?type=/?propertyType= so nav clicks and shared/bookmarked Browse links
  // survive a cold page load, not just an in-session state change. Only
  // applied once on mount, so it doesn't fight with the user's own filter
  // changes afterward.
  useEffect(() => {
    const q = searchParams.get('q');
    if (q && q.trim()) {
      setFilterLocation(q.trim());
    }
    const type = searchParams.get('type');
    if (type && type.trim()) {
      setFilterType(type.trim());
    }
    const propertyType = searchParams.get('propertyType');
    if (propertyType && propertyType.trim()) {
      setFilterPropertyType(propertyType.trim());
    }
    const area = searchParams.get('area');
    if (area && area.trim()) {
      setFilterArea(area.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keeps the URL in sync as filters change (via nav, hero, or the toolbar
  // inputs) so the current category is always shareable/bookmarkable.
  // filterType is only reflected once it exactly matches a canonical value -
  // otherwise partial in-progress datalist typing (e.g. "Ren") would spam
  // the URL with non-canonical values.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const normalizedType = filterType.trim().toLowerCase();
    if (CANONICAL_LISTING_TYPES.includes(normalizedType)) {
      next.set('type', normalizedType);
    } else {
      next.delete('type');
    }
    if (filterPropertyType) {
      next.set('propertyType', filterPropertyType);
    } else {
      next.delete('propertyType');
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, filterPropertyType]);

  const [foldedFilterKeys, setFoldedFilterKeys] = useState([]);
  const [isNotifyDrawerOpen, setIsNotifyDrawerOpen] = useState(false);
  const [notifyStatus, setNotifyStatus] = useState({ type: '', message: '' });
  const [isSubmittingAlert, setIsSubmittingAlert] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortRef = useRef(null);
  const pageSize = 16;

  const isShortLetBrowse = filterType.trim().toLowerCase() === 'short-let';
  const showAmenityFilters = filterType.trim() === '' || isShortLetBrowse;
  // Property Documentation only ever exists on Sale listings - no point
  // showing the filter while browsing Rent/Short-let, where it can never
  // match anything.
  const showPropertyDocumentFilters = filterType.trim() === '' || filterType.trim().toLowerCase() === 'buy';

  const shortLetSortParam =
    sortBy === 'price-low' ? 'price_asc' : sortBy === 'price-high' ? 'price_desc' : sortBy === 'guests' ? 'guests' : undefined;

  const shortLet = useShortLetProperties(
    {
      state: filterLocation,
      area: filterArea,
      minPrice,
      maxPrice,
      bedrooms: minBedrooms,
      sort: shortLetSortParam,
    },
    { enabled: isShortLetBrowse },
  );

  const shortLetResults = useMemo(
    () =>
      applyClientRefinements(shortLet.properties, {
        filterFlags: filterState,
        minBathrooms,
        postedWithin,
        filterAmenities,
        filterPropertyDocuments,
      }),
    [shortLet.properties, filterState, minBathrooms, postedWithin, filterAmenities, filterPropertyDocuments],
  );

  const activeResults = isShortLetBrowse ? shortLetResults : filteredProperties;
  const activeLoading = isShortLetBrowse ? shortLet.loading : loading;
  const activeError = isShortLetBrowse ? shortLet.error : error;

  const sortOptions = [
    { value: 'newest', label: 'Newest first', icon: <ClockIcon size={16} /> },
    {
      value: 'price-low',
      label: 'Price: Low to High',
      icon: <TrendingUpIcon size={16} />,
    },
    {
      value: 'price-high',
      label: 'Price: High to Low',
      icon: <TrendingDownIcon size={16} />,
    },
    ...(isShortLetBrowse
      ? [{ value: 'guests', label: 'Most guests', icon: <HomeIcon size={16} /> }]
      : []),
  ];

  const activeSortOption = sortOptions.find((o) => o.value === sortBy) || sortOptions[0];

  useEffect(() => {
    function handleClickOutside(e) {
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setIsSortOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    filterLocation,
    filterArea,
    filterType,
    filterPropertyType,
    sortBy,
    minPrice,
    maxPrice,
    minBedrooms,
    minBathrooms,
    postedWithin,
    viewMode,
  ]);

  const hasActiveFilters =
    filterLocation ||
    filterArea ||
    filterType ||
    filterPropertyType ||
    minPrice ||
    maxPrice ||
    minBedrooms ||
    minBathrooms ||
    (postedWithin && postedWithin !== 'any');
  // Badge on the mobile "More filters" button - counts every applied
  // filter, whether it's a quick filter currently folded into the drawer or
  // one of the drawer-only advanced filters, so the badge is accurate
  // regardless of screen width.
  const activeFilterCount = [
    filterLocation,
    filterArea,
    filterType,
    filterPropertyType,
    minPrice,
    maxPrice,
    minBedrooms,
    minBathrooms,
    postedWithin && postedWithin !== 'any' ? postedWithin : '',
  ].filter(Boolean).length;
  const quickFilterFields = buildQuickFilterFields({
    filterLocation,
    filterArea,
    filterType,
    filterPropertyType,
    setFilterLocation,
    setFilterArea,
    setFilterType,
    setFilterPropertyType,
    browseAreas,
  });
  const foldedQuickFilterFields = quickFilterFields.filter((field) =>
    foldedFilterKeys.includes(field.key),
  );
  const spotlightProperties = useMemo(
    () => activeResults.filter((item) => item.isFeatured),
    [activeResults],
  );
  const totalPages = Math.max(1, Math.ceil(activeResults.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedProperties = activeResults.slice(
    (safeCurrentPage - 1) * pageSize,
    safeCurrentPage * pageSize,
  );
  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Browse', current: true },
  ];

  const openNotifyDrawer = () => {
    if (!user) {
      showToast('Log in to create a property alert.', 'warning', 8000, {
        label: 'Go to login',
        onClick: () => navigate('/login', { state: { from: '/browse' } }),
      });
      return;
    }
    setNotifyStatus({ type: '', message: '' });
    setIsNotifyDrawerOpen(true);
  };

  const closeNotifyDrawer = () => {
    setIsNotifyDrawerOpen(false);
    setNotifyStatus({ type: '', message: '' });
  };

  const handleNotifySubmit = async (event) => {
    event.preventDefault();

    setIsSubmittingAlert(true);
    setNotifyStatus({ type: '', message: '' });

    const payload = {
      state: filterLocation || '',
      area: filterArea || '',
      type: filterType || '',
      propertyType: filterPropertyType || '',
      minPrice,
      maxPrice,
      minBedrooms,
      minBathrooms,
      postedWithin,
    };

    try {
      const response = await authedFetch(`${API_BASE}/alerts`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (!response.ok) {
        setNotifyStatus({
          type: 'error',
          message: result.error || 'Failed to create property alert.',
        });
        return;
      }

      setNotifyStatus({
        type: result.duplicate ? 'info' : 'success',
        message: result.message || 'Property alert created successfully.',
      });
    } catch (requestError) {
      setNotifyStatus({
        type: 'error',
        message: 'Could not connect to the alert service. Try again.',
      });
    } finally {
      setIsSubmittingAlert(false);
    }
  };

  const browseTitle = filterLocation ? `Properties in ${filterLocation}` : 'Browse Properties';
  const browseDesc = filterLocation
    ? `Browse moderated properties in ${filterLocation}, Nigeria. Find apartments, houses, and land for rent, sale, and short-let.`
    : 'Browse moderated property listings across Nigeria. Rent, buy, or short-let apartments, houses, duplexes, and land.';
  const seo = pageMeta({ title: browseTitle, description: browseDesc, path: '/browse' });

  return (
    <>
      <SeoHead title={seo.fullTitle} description={seo.desc} url={seo.url} image={seo.img} />
      {/* Visually hidden: the sticky filter bar below already gives this
          page its visual identity, and a visible heading here would bring
          back the extra vertical space that was deliberately trimmed from
          this page. Still a real h1 for accessibility/SEO. */}
      <h1 className="sr-only">{browseTitle}</h1>
      {/* Browse is a primary bottom-nav destination on mobile, not a page
          you'd navigate back out of, so it opts out of the mobile back bar
          that every other Breadcrumbs caller gets for free. */}
      <Breadcrumbs items={breadcrumbItems} hideMobileBackBar />
      <section className="section browse-controls">
        <BrowseQuickFilters
          fields={quickFilterFields}
          measureKey={`${filterLocation}|${filterArea}|${filterType}|${filterPropertyType}`}
          activeFilterCount={activeFilterCount}
          onOpenFilters={() => setIsFilterDrawerOpen(true)}
          onFoldedChange={setFoldedFilterKeys}
        />
        <div className="browse-toolbar browse-toolbar--desktop">
          <div className="browse-toolbar-row">
            <select
              className="ui-select"
              value={filterLocation}
              onChange={(event) => {
                setFilterLocation(event.target.value);
                setFilterArea('');
              }}
            >
              <option value="">All States</option>
              {locations.map((stateName) => (
                <option key={stateName} value={stateName}>
                  {stateName}
                </option>
              ))}
            </select>

            <select
              className="ui-select"
              value={filterArea}
              onChange={(event) => setFilterArea(event.target.value)}
              disabled={!filterLocation}
            >
              <option value="">All Areas</option>
              {/* browseAreas is the fixed LGA list for the selected state, not
                  every free-text neighborhood a listing can carry (e.g. a
                  ?area= link seeded from a property's geocoded area, or one
                  an agent typed by hand) - without this, a value outside
                  that list still filters correctly (exact string match, see
                  usePropertyFilters.js) but silently shows as "All Areas"
                  here since no <option> matches it. */}
              {filterArea && !browseAreas.includes(filterArea) && (
                <option value={filterArea}>{filterArea}</option>
              )}
              {browseAreas.map((stateArea) => (
                <option key={stateArea} value={stateArea}>
                  {stateArea}
                </option>
              ))}
            </select>
          </div>

          <div className="browse-toolbar-row">
            <select
              className="ui-select"
              value={filterType}
              onChange={(event) => setFilterType(event.target.value)}
            >
              <option value="">All Listing Types</option>
              <option value="Buy">For Sale</option>
              <option value="Short-let">Short-let</option>
              <option value="Rent">Rent</option>
            </select>

            <select
              className="ui-select"
              value={filterPropertyType}
              onChange={(event) => setFilterPropertyType(event.target.value)}
            >
              <option value="">Any Type</option>
              {propertyTypes
                .filter(([value]) => value !== 'Other')
                .map(([value, label]) => (
                  <option key={value} value={label}>
                    {label}
                  </option>
                ))}
            </select>

            <Tooltip content="Open advanced filters and preferences">
              <button type="button" className="secondary browse-toolbar__more" onClick={() => setIsFilterDrawerOpen(true)}>
                <FilterIcon size={16} /> <span className="browse-toolbar__more-label">More Filters</span>
              </button>
            </Tooltip>
            {hasActiveFilters && (
              <button type="button" className="clear-filters-btn" onClick={resetAllFilters}>
                ✕ <span className="browse-toolbar__more-label">Clear Filters</span>
              </button>
            )}
          </div>
        </div>
      </section>

      {!activeLoading && !activeError && <AdBanner slot="sidebar" />}

      {!activeLoading && !activeError && (
        <PropertySpotlightBanner
          properties={spotlightProperties}
          placeholderImage={placeholderImage}
          onOpenProperty={onOpenProperty}
        />
      )}

      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        foldedQuickFilterFields={foldedQuickFilterFields}
        minPrice={minPrice}
        maxPrice={maxPrice}
        minBedrooms={minBedrooms}
        minBathrooms={minBathrooms}
        postedWithin={postedWithin}
        setMinPrice={setMinPrice}
        setMaxPrice={setMaxPrice}
        setMinBedrooms={setMinBedrooms}
        setMinBathrooms={setMinBathrooms}
        setPostedWithin={setPostedWithin}
        filterState={filterState}
        filterSetters={filterSetters}
        filterAmenities={filterAmenities}
        toggleFilterAmenity={toggleFilterAmenity}
        showAmenityFilters={showAmenityFilters}
        filterPropertyDocuments={filterPropertyDocuments}
        toggleFilterPropertyDocument={toggleFilterPropertyDocument}
        showPropertyDocumentFilters={showPropertyDocumentFilters}
        resetAllFilters={resetAllFilters}
        resultCount={activeResults.length}
      />

      {isNotifyDrawerOpen && (
        <button
          type="button"
          className="filter-overlay"
          aria-label="Close property alert drawer"
          onClick={closeNotifyDrawer}
        />
      )}
      <aside className={`notify-drawer ${isNotifyDrawerOpen ? 'open' : ''}`}>
        <div className="drawer-header notify-header">
          <div>
            <p className="notify-badge">
              <span aria-hidden="true">🔔</span>
              Property Alerts
            </p>
            <h3>Notify me</h3>
          </div>
          <button type="button" className="btn-link" onClick={closeNotifyDrawer}>
            Close
          </button>
        </div>
        <p className="notify-lead-text">
          Be the first to find new properties that match your search criteria by creating an alert.
        </p>

        <div className="notify-benefits" aria-label="Why create this alert">
          <p>
            <span aria-hidden="true">⚡</span>
            Instant updates for new matching listings
          </p>
          <p>
            <span aria-hidden="true">🛡️</span>
            No spam, just relevant property matches
          </p>
          <p>
            <span aria-hidden="true">📍</span>
            Focus alerts by state, area, and filters
          </p>
          <p>
            <span aria-hidden="true">📬</span>
            Delivered by email and to your notifications bell
          </p>
        </div>

        <form className="notify-form" onSubmit={handleNotifySubmit}>
          <p className="notify-recipient">
            <span aria-hidden="true">✉️</span>
            Alerts will be sent to <strong>{user?.email}</strong>
          </p>

          <p className="notify-frequency-label">
            Sent as soon as a match is found. To switch to a daily or weekly summary instead,
            change your email cadence in{' '}
            <Link to="/settings" onClick={closeNotifyDrawer}>
              notification settings
            </Link>
            .
          </p>

          {(filterLocation || filterArea) && (
            <p className="subtext notify-matching-chip">
              <span aria-hidden="true">🎯</span>
              Matching: {filterLocation || 'All States'}
              {filterArea ? `, ${filterArea}` : ''}
            </p>
          )}

          {notifyStatus.message && (
            <p
              className={`notify-status ${notifyStatus.type}`}
              role={notifyStatus.type === 'error' ? 'alert' : 'status'}
              aria-live={notifyStatus.type === 'error' ? 'assertive' : 'polite'}
            >
              <span className="notify-status-icon" aria-hidden="true">
                {notifyStatus.type === 'info' && 'ℹ️'}
                {notifyStatus.type === 'success' && '✅'}
                {notifyStatus.type === 'error' && '⚠️'}
              </span>
              <span className="sr-only">
                {notifyStatus.type === 'info' && 'Info: '}
                {notifyStatus.type === 'success' && 'Success: '}
                {notifyStatus.type === 'error' && 'Error: '}
              </span>
              <span>{notifyStatus.message}</span>
            </p>
          )}

          <button type="submit" className="btn-primary" disabled={isSubmittingAlert}>
            {isSubmittingAlert ? 'Creating alert...' : 'Create property alert'}
          </button>
        </form>
      </aside>

      {!activeLoading && !activeError && (
        <div className="view-mode-row">
          <p className="view-mode-row__count">
            <HomeIcon size={14} />
            <strong>{activeResults.length.toLocaleString()}</strong>
            {' '}
            properties
            {filterType ? ` to ${filterType}` : ''}
            {filterLocation ? ` in ${filterLocation}` : ' in Nigeria'}
          </p>
          <div className="result-actions__center">
            <button
              type="button"
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
            <button
              type="button"
              className={viewMode === 'map' ? 'active' : ''}
              onClick={() => setViewMode('map')}
            >
              Map
            </button>
            <button
              type="button"
              className={`split-view-btn${viewMode === 'split' ? ' active' : ''}`}
              onClick={() => setViewMode('split')}
            >
              Split
            </button>
          </div>
          <div className="result-actions__right">
            <div className="sort-dropdown" ref={sortRef}>
              <button
                type="button"
                className="sort-dropdown__trigger"
                onClick={() => setIsSortOpen((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={isSortOpen}
              >
                {activeSortOption.icon}
                <span>{activeSortOption.label}</span>
                <ChevronDownIcon size={14} />
              </button>
              {isSortOpen && (
                <ul className="sort-dropdown__menu" role="listbox">
                  {sortOptions.map((opt) => (
                    <li
                      key={opt.value}
                      role="option"
                      aria-selected={sortBy === opt.value}
                      className={
                        'sort-dropdown__item' +
                        (sortBy === opt.value ? ' sort-dropdown__item--active' : '')
                      }
                      onClick={() => {
                        setSortBy(opt.value);
                        setIsSortOpen(false);
                      }}
                    >
                      {opt.icon}
                      <span>{opt.label}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button type="button" className="secondary" onClick={openNotifyDrawer}>
              <BellIcon size={16} /> Notify me
            </button>
          </div>
        </div>
      )}

      {activeError && <p className="error-text">⚠️ {activeError}</p>}
      {activeLoading && <LoadingSpinner size="lg" label="Loading properties..." />}
      {!activeLoading && !activeError && activeResults.length === 0 && (
        <p>No matching properties found.</p>
      )}

      {!activeLoading && !activeError && activeResults.length > 0 && viewMode === 'list' && (
        <section className="listing-grid">
          {paginatedProperties.map((item) => (
            <ListingCard key={item.id} item={item} onOpenProperty={onOpenProperty} />
          ))}
        </section>
      )}

      {!activeLoading && !activeError && activeResults.length > 0 && viewMode === 'map' && (
        <section className="map-view-section">
          <PropertyGoogleMap properties={paginatedProperties} height={560} />
        </section>
      )}

      {!activeLoading && !activeError && activeResults.length > 0 && viewMode === 'split' && (
        <section className="ui-grid browse-split-layout">
          <div className="ui-section split-list-panel">
            <section className="ui-grid listing-grid listing-grid--split">
              {paginatedProperties.map((item) => (
                <ListingCard key={item.id} item={item} onOpenProperty={onOpenProperty} />
              ))}
            </section>
          </div>
          <div className="ui-section split-map-panel">
            <PropertyGoogleMap properties={paginatedProperties} />
          </div>
        </section>
      )}

      {!activeLoading && !activeError && activeResults.length > 0 && (
        <Pagination
          currentPage={safeCurrentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(Math.min(Math.max(page, 1), totalPages))}
        />
      )}

    </>
  );
}

export default BrowsePage;
